/**
 * React Query hooks for the Centerstage library page. The video detail shares
 * the Author with AI chat's cache key, so a video previewed in one is not
 * fetched again in the other.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { authorWithAiKeys } from './authorWithAiQueries';
import {
  fetchConceptCounts,
  fetchLibraryFilters,
  getVideo,
  listLibrary,
  type LibraryQuery,
} from './centerstageApi';

export const LIBRARY_PAGE_SIZE = 24;
const TEN_MINUTES = 10 * 60 * 1000;
/** One retry, not the app's three: a stalled Centerstage should reach the error panel and its Retry quickly. */
const RETRY_ONCE = 1;

export const centerstageKeys = {
  all: ['centerstage'] as const,
  filters: () => [...centerstageKeys.all, 'library-filters'] as const,
  conceptCounts: () => [...centerstageKeys.all, 'concept-counts'] as const,
  library: (query: LibraryQuery) =>
    [...centerstageKeys.all, 'library', query.q ?? '', query.categoryId ?? null, query.conceptId ?? null, query.type ?? '', query.setting ?? ''] as const,
  /** One entry per video, shared by the Centerstage page and the Author with AI preview. */
  videoDetail: (videoId: string) => [...authorWithAiKeys.all, 'video-detail', videoId] as const,
};

export function useLibraryFilters() {
  return useQuery({
    queryKey: centerstageKeys.filters(),
    queryFn: fetchLibraryFilters,
    staleTime: TEN_MINUTES,
    retry: RETRY_ONCE,
  });
}

/** Counts are about 200 upstream calls on a cold server cache, so they load only once `enabled` (the concepts dialog first opens). */
export function useConceptCounts(enabled: boolean) {
  return useQuery({
    queryKey: centerstageKeys.conceptCounts(),
    queryFn: fetchConceptCounts,
    staleTime: TEN_MINUTES,
    retry: RETRY_ONCE,
    enabled,
  });
}

export function useLibrary(query: LibraryQuery) {
  return useInfiniteQuery({
    queryKey: centerstageKeys.library(query),
    queryFn: ({ pageParam }) => listLibrary(query, pageParam, LIBRARY_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.videos.length, 0);
      return lastPage.videos.length > 0 && loaded < lastPage.total ? pages.length + 1 : undefined;
    },
    staleTime: 60 * 1000,
    retry: RETRY_ONCE,
  });
}

export function useVideoDetail(videoId: string | null) {
  return useQuery({
    queryKey: centerstageKeys.videoDetail(videoId ?? ''),
    queryFn: () => getVideo(videoId!),
    staleTime: TEN_MINUTES,
    enabled: videoId !== null,
  });
}
